import { CaretUpDownIcon, PlusIcon, UserCircleIcon } from '@phosphor-icons/react';

import Image from '@/components/ui/Image';
import Link from '@/components/ui/Link';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { spaceIcon } from '@/lib/spaces/navigation';
import { cn } from '@/lib/utils';
import { PersonalScopeMenuItem, SpaceScopeMenuItems } from './ScopeMenuItems';

type ScopeSwitcherProps = { compact?: boolean; collapsed?: boolean };

function ScopeIcon({ compact }: Pick<ScopeSwitcherProps, 'compact'>) {
  const { scope, space } = useSpaceScope();
  if (!space && !compact && scope === 'personal') {
    return <Image src="/favicon.png" alt="" className="size-8 shrink-0" />;
  }
  const Icon = space ? spaceIcon(space.kind) : UserCircleIcon;
  return <Icon aria-hidden className={cn('shrink-0 text-primary', compact ? 'size-4' : 'size-7')} />;
}

function ScopeIdentity({ compact, collapsed }: ScopeSwitcherProps) {
  const { scope, space } = useSpaceScope();
  if (collapsed) return null;

  const title = space?.name ?? (scope === 'space' ? 'Espaço' : compact ? 'Pessoal' : 'Vértice');
  const subtitle = space
    ? space.members.map((member) => member.name ?? 'Integrante').join(', ')
    : scope === 'space' ? 'Espaço compartilhado' : 'Conta pessoal';

  return (
    <div className="min-w-0 flex-1">
      <div className="truncate font-bold text-text-primary">{title}</div>
      {!compact && <p className="truncate text-2xs text-text-secondary">{subtitle}</p>}
    </div>
  );
}

export function ScopeSwitcher({ compact = false, collapsed = false }: ScopeSwitcherProps) {
  const { enabled, scope, space } = useSpaceScope();
  if (!enabled) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Trocar de escopo: ${space?.name ?? (scope === 'space' ? 'Espaço' : 'Pessoal')}`}
          data-testid="scope-switcher"
          className={cn(
            'flex min-w-0 cursor-pointer items-center gap-2.5 rounded-lg p-1 text-left text-sm hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-ring',
            compact ? 'h-9 max-w-40 gap-1.5 rounded-xl border border-border-subtle bg-surface px-2 text-xs md:hidden' : 'w-full',
            collapsed && 'justify-center',
          )}
        >
          <ScopeIcon compact={compact} />
          <ScopeIdentity compact={compact} collapsed={collapsed} />
          {!collapsed && <CaretUpDownIcon aria-hidden className="size-3.5 shrink-0 text-text-secondary" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-w-[calc(100vw-var(--spacing)*8)] rounded-md border border-border bg-surface p-1.5 shadow-none">
        <PersonalScopeMenuItem />
        <DropdownMenuLabel className="text-xs text-text-secondary">Espaços</DropdownMenuLabel>
        <SpaceScopeMenuItems />
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link href="/spaces/new" className="gap-2.5 rounded-lg p-2"><PlusIcon aria-hidden />Criar espaço</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-xs text-text-secondary">Trocar de escopo não compartilha nada. O que é seu continua só seu.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
